import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiUpload, FiCheck, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Loading from '../../ions/Loading';
import FileUpload from '../../molecules/FileUpload';
import SelectTextField from '../../molecules/SelectTextField';
import SendToSignatureModal from '../signature/SendToSignatureModal';
import { contractApi } from '../../../services/contractApi';
import { contractsManagementApi } from '../../../services/contractsManagementApi';
import type { OperationResult } from '../../../services/contractsManagementApi';
import { personApi } from '../../../services/personApi';
import { signatureApi } from '../../../services/signatureApi';
import { toBase64 } from '../../../utils/fileUtils';
import { formatCpfCnpj, formatCurrency, mapOperationTypeToContractType, normalizeSignerRole, translateContractStatus } from './ContractSignPage.helpers';
import StepIndicator from './StepIndicator';
import type { StepConfig } from './StepIndicator';

type Step = 'upload' | 'extraction' | 'comparison';

const STEPS: StepConfig[] = [
    { key: 'upload', label: 'Upload', icon: <FiUpload size={14} /> },
    { key: 'extraction', label: 'Extração', icon: <FiCheck size={14} /> },
    { key: 'comparison', label: 'Validação', icon: <FiCheck size={14} /> },
];

interface ComparisonField {
    label: string;
    operationValue: string;
    documentValue: string;
    match: boolean;
}

const ContractSignPage = () => {
    const { operationId } = useParams<{ operationId: string }>();
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>('upload');
    const [loading, setLoading] = useState(false);

    // Operation data (from contracts-management)
    const [operation, setOperation] = useState<OperationResult | null>(null);

    // Contract flow state
    const [file, setFile] = useState<File | null>(null);
    const [documentId, setDocumentId] = useState('');
    const [contractId, setContractId] = useState('');
    const [extractedData, setExtractedData] = useState<Record<string, unknown> | null>(null);
    const [iterationNumber, setIterationNumber] = useState(0);
    const [comparison, setComparison] = useState<ComparisonField[]>([]);
    const [signerValidations, setSignerValidations] = useState<{ name: string; govTaxId: string; role: string; valid: boolean; email?: string; reason?: string; signingGroupId?: string; signingGroupName?: string; personName?: string; nameDiverges?: boolean; anchorName?: string }[]>([]);
    const [availableRoles, setAvailableRoles] = useState<{ value: string; label: string; signingGroupId?: string; signingGroupName?: string; minCount?: number; required?: boolean }[]>([]);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [contractValidation, setContractValidation] = useState<{ canSendToSignature: boolean; issues: any[] } | null>(null);
    const [existingContract, setExistingContract] = useState<{ contractId: string; fileName?: string; uploadedBy?: string; signatureProcessId?: string; status?: string } | null>(null);
    const [checkingExisting, setCheckingExisting] = useState(false);

    useEffect(() => {
        if (operationId) loadOperation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationId]);

    const loadOperation = async () => {
        try {
            const op = await contractsManagementApi.getReceivableAssignmentById(operationId!);
            if (op) {
                setOperation(op);
                checkExistingContract(op.operation_number);
            }
        } catch (e) {
            console.warn('[ContractSignPage] Erro ao carregar operação:', e);
        }
    };

    const checkExistingContract = async (operationNumber: string) => {
        setCheckingExisting(true);
        try {
            const result = await contractApi.getList({ page: 1, size: 500 });
            const contracts = result?.data || result || [];
            const existing = contracts.find((c: any) => c.externalId === operationNumber && c.status !== 'CANCELLED');
            if (existing) {
                try {
                    const detail = await contractApi.getContractPageForm(existing.contractId);
                    const doc = detail?.documents?.[0];
                    const sigRef = detail?.signatureReferences?.[0];

                    // Fetch real signature process status if available
                    let signatureStatus: string | undefined;
                    if (sigRef?.signatureProcessId) {
                        try {
                            const sigDetail = await signatureApi.getProcessDetail(sigRef.signatureProcessId);
                            signatureStatus = sigDetail?.status;
                        } catch { /* fallback to contract status */ }
                    }

                    setExistingContract({
                        contractId: existing.contractId,
                        fileName: doc?.fileName || existing.contractType || 'Documento',
                        uploadedBy: detail?.uploadedBy || existing.uploadedBy,
                        signatureProcessId: sigRef?.signatureProcessId,
                        status: signatureStatus || existing.status,
                    });
                } catch {
                    setExistingContract({
                        contractId: existing.contractId,
                        fileName: existing.contractType || 'Documento',
                        status: existing.status,
                    });
                }
            }
        } catch (e) {
            console.warn('[ContractSignPage] Erro ao verificar contrato existente:', e);
        } finally {
            setCheckingExisting(false);
        }
    };

    // Step 1: Upload
    const handleUpload = async () => {
        if (!file) return;
        setLoading(true);
        try {
            const base64 = await toBase64(file);
            const docResult = await contractApi.createDocument(file.name, base64, mapOperationTypeToContractType(operation?.operation_type || ''), operation?.operation_number);
            setDocumentId(docResult.documentId);
            setContractId(docResult.contractId);

            // Auto-extract
            const extResult = await contractApi.createExtraction(docResult.documentId, docResult.contractId);
            const extraction = await contractApi.getExtractionPageForm(extResult.extractionId);
            setExtractedData(extraction.extractedData);
            setIterationNumber(extraction.iterationNumber);
            setStep('extraction');
            toast.success('Documento enviado e dados extraídos');
        } catch (e: any) {
            toast.error(e.message || 'Erro ao processar documento');
        } finally {
            setLoading(false);
        }
    };

    // Helper: load signatories of a legal person by CNPJ (for sending to backend)
    const loadSignatoriesByCnpj = async (cnpj: string): Promise<import('../../../services/personApi').Signatory[]> => {
        const clean = cnpj.replace(/\D/g, '');
        if (clean.length !== 14) return [];
        const legalPerson = await personApi.searchLegalPersonByCnpj(clean);
        if (!legalPerson) return [];
        const signatories = await personApi.getSignatories(legalPerson.personId);
        return signatories.filter(s => s.isActive);
    };

    // Step 2 → 3: Compare extracted data with operation
    const handleProceedToComparison = async () => {
        if (!extractedData || !contractId) return;
        setLoading(true);
        try {
            // Ensure operation is loaded
            let op = operation;
            if (!op && operationId) {
                op = await contractsManagementApi.getReceivableAssignmentById(operationId);
                if (op) setOperation(op);
            }

            const assignor = op?.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR' || p.participant_type_name === 'BORROWER');
            const recipient = op?.participants?.find((p: any) => p.participant_type_name === 'RECIPIENT');
            const maturityValue = op?.operation_dictionary?.total_amount ?? null;
            const disbursementValue = op?.nominal_value ?? op?.operation_dictionary?.disbursement?.totalAmount ?? null;
            const expectedContractType = mapOperationTypeToContractType(op?.operation_type || '') || op?.operation_type || '-';
            const extractedContractType = String(extractedData.operation_type || '-');

            const fields: ComparisonField[] = [
                { label: 'Cedente (Nome)', operationValue: assignor?.name || '-', documentValue: String(extractedData.person_name || '-'), match: (() => { const a = (assignor?.name || '').trim().toLowerCase().replace(/\s+/g, ' '); const b = String(extractedData.person_name || '').trim().toLowerCase().replace(/\s+/g, ' '); return a === b || a.includes(b) || b.includes(a); })() },
                { label: 'Cedente (CPF/CNPJ)', operationValue: formatCpfCnpj(assignor?.document), documentValue: formatCpfCnpj(String(extractedData.person_gov_tax_id || '')), match: (assignor?.document || '').replace(/\D/g, '') === String(extractedData.person_gov_tax_id || '').replace(/\D/g, '') },
                { label: 'Beneficiário / Cessionária', operationValue: recipient?.name || '-', documentValue: '-', match: !!recipient?.name },
                { label: 'Valor no Vencimento', operationValue: formatCurrency(maturityValue), documentValue: formatCurrency(extractedData.operation_value as number), match: Math.abs(Number(maturityValue ?? 0) - Number(extractedData.operation_value ?? 0)) < 0.01 },
                { label: 'Valor de Desembolso', operationValue: formatCurrency(disbursementValue), documentValue: formatCurrency(extractedData.disbursement_value as number), match: Math.abs(Number(disbursementValue ?? 0) - Number(extractedData.disbursement_value ?? 0)) < 0.01 },
                { label: 'Tipo de Contrato', operationValue: expectedContractType.replace(/_/g, ' '), documentValue: extractedContractType.replace(/_/g, ' '), match: expectedContractType === extractedContractType },
            ];
            setComparison(fields);

            // Load signatories from Person API for both participants
            const assignorSignatories = assignor?.document ? await loadSignatoriesByCnpj(assignor.document) : [];
            const recipientSignatories = recipient?.document ? await loadSignatoriesByCnpj(recipient.document) : [];

            // Prepare signers for enrichment (raw AI extraction)
            const rawSigners = (Array.isArray(extractedData.signers) ? extractedData.signers : []).map((s: any, i: number) => ({
                name: s.name || '',
                gov_tax_id: (s.gov_tax_id || '').replace(/\D/g, ''),
                role: s.role || '',
                email: s.email || '',
                signer_order: s.signer_order || i + 1,
            }));

            // Call backend to enrich roles (domain logic lives in Contract Service)
            const enrichResult = await contractApi.enrichSigners(contractId, rawSigners, [
                { participant_type: 'RECIPIENT', signatories: recipientSignatories as any[] },
                { participant_type: 'ASSIGNOR', signatories: assignorSignatories as any[] },
            ], maturityValue);

            const enrichedSigners = enrichResult.signers || [];

            // Update extractedData with enriched roles for downstream use
            const enrichedExtractedData = { ...extractedData, signers: enrichedSigners.map((s: any) => ({ ...s, gov_tax_id: s.govTaxId })) };
            setExtractedData(enrichedExtractedData);

            const extractedType = String(extractedData.operation_type || '');

            // Load available roles from ContractType configuration
            let loadedRoles: any[] = [];
            try {
                const ct = await contractApi.getContractType(extractedType);
                if (ct?.structures?.[0]?.roles) {
                    loadedRoles = ct.structures[0].roles.map((r: any) => ({
                        value: r.role,
                        label: r.label || r.role,
                        signingGroupId: r.signingGroupId,
                        signingGroupName: r.signingGroupName,
                        minCount: r.minCount || 1,
                        required: r.required !== false,
                    }));
                    setAvailableRoles(loadedRoles);
                }
            } catch { /* ContractType may not exist yet, roles will be empty */ }

            // Resolve signing groups: replace company placeholder with actual PF signatories
            // from the RECIPIENT (Agrolend). Each PF signatory signs individually using the
            // company name as anchor (to position signature on the company's field in the PDF).
            const signingGroupRoles = loadedRoles.filter((r: any) => r.signingGroupId);
            for (const sgRole of signingGroupRoles) {
                // Remove the company/group placeholder signer (non-PF with this role)
                const groupIdx = enrichedSigners.findIndex((s: any) => {
                    if (s.role !== sgRole.value) return false;
                    const digits = (s.govTaxId || '').replace(/\D/g, '');
                    return digits.length !== 11; // Not a PF
                });
                const companyName = groupIdx >= 0 ? enrichedSigners[groupIdx].name : (sgRole.signingGroupName || sgRole.label);
                if (groupIdx >= 0) {
                    enrichedSigners.splice(groupIdx, 1);
                }

                // Add individual PF signatories from RECIPIENT (Agrolend's representatives)
                // These are the people who actually sign on behalf of the company
                // Only include representatives who sign on behalf of the company (Representante legal, Cedente)
                // Exclude Testemunha/Validador — they sign in their own capacity, not for the company
                const REPRESENTATIVE_ROLES = ['representante legal', 'cedente', 'procurador'];
                const repsAdded = recipientSignatories
                    .filter((sig: any) => {
                        if (sig.isActive === false) return false;
                        const signAsRoles = (sig.signAsEntries || []).map((e: any) => (e.signAs || '').toLowerCase());
                        return signAsRoles.some((role: string) => REPRESENTATIVE_ROLES.some(r => role.includes(r)));
                    })
                    .map((sig: any, idx: number) => ({
                        name: sig.fullName || sig.name || '',
                        govTaxId: (sig.socialId || '').replace(/\D/g, ''),
                        role: sgRole.value,
                        email: sig.email || '',
                        signerOrder: enrichedSigners.length + idx + 1,
                        enrichedFrom: 'RECIPIENT',
                        validationIssue: null,
                        personName: null,
                        nameDiverges: false,
                        signingGroupId: null,
                        signingGroupName: null,
                        anchorName: companyName.split(' ')[0], // short anchor (e.g., "AGROLEND") — avoids PDF whitespace issues
                    }));

                if (repsAdded.length > 0) {
                    enrichedSigners.push(...repsAdded);
                } else {
                    // No representatives found — keep a placeholder so user is aware
                    enrichedSigners.push({
                        name: companyName,
                        govTaxId: '',
                        role: sgRole.value,
                        email: '',
                        signerOrder: enrichedSigners.length + 1,
                        enrichedFrom: 'CONTRACT_TYPE',
                        validationIssue: 'Nenhum representante legal encontrado para esta empresa',
                        personName: null,
                        nameDiverges: false,
                        signingGroupId: sgRole.signingGroupId,
                        signingGroupName: sgRole.signingGroupName,
                    });
                }
            }

            // Filter out PJ signers (CNPJ = 14 digits) that are NOT signing groups.
            // Only PF (CPF) signers and signing group signers should remain.
            const filteredSigners = enrichedSigners.filter((s: any) => {
                if (s.signingGroupId) return true; // signing group always stays
                const digits = (s.govTaxId || '').replace(/\D/g, '');
                if (digits.length === 14) return false; // CNPJ = PJ, remove
                return true; // CPF or no doc = keep
            });

            const mappedSigners = filteredSigners.map((s: any) => ({
                name: s.name, email: s.email || '', role: normalizeSignerRole(s.role || ''), govTaxId: s.govTaxId || '',
            }));
            await contractApi.updateContract(contractId, {
                contractType: extractedType || undefined,
                signers: mappedSigners,
                metaData: {
                    person_name: assignor?.name || String(extractedData.person_name || ''),
                    person_gov_tax_id: (assignor?.document || String(extractedData.person_gov_tax_id || '')).replace(/\D/g, ''),
                    operation_value: maturityValue,
                    disbursement_value: disbursementValue,
                },
            });

            // Call backend for consolidated validation
            const validationResult = await contractApi.validateForSignature(contractId, {
                signers: filteredSigners.map((s: any) => ({
                    name: s.name, govTaxId: s.govTaxId || '', role: s.role || '',
                    email: s.email || '', signerOrder: s.signerOrder || 1,
                    validationIssue: s.validationIssue || null,
                })),
                operationData: {
                    personName: assignor?.name || '',
                    personGovTaxId: (assignor?.document || '').replace(/\D/g, ''),
                    maturityValue: maturityValue,
                    disbursementValue: disbursementValue,
                    operationType: mapOperationTypeToContractType(op?.operation_type || '') || '',
                },
                extractedData: {
                    personName: String(extractedData.person_name || ''),
                    personGovTaxId: String(extractedData.person_gov_tax_id || '').replace(/\D/g, ''),
                    operationValue: (extractedData.operation_value as number) ?? null,
                    disbursementValue: (extractedData.disbursement_value as number) ?? null,
                    operationType: extractedType,
                },
                contractType: extractedType,
            });

            // Set comparison from backend response
            const compIssues = validationResult.comparisonValidation?.issues || [];
            setComparison(compIssues.map((issue: any) => ({
                label: issue.message,
                operationValue: issue.expected || '-',
                documentValue: issue.found || '-',
                match: false,
            })));
            // Add matched fields (no issues = match)
            if (compIssues.length === 0) {
                setComparison([
                    { label: 'Cedente (Nome)', operationValue: assignor?.name || '-', documentValue: String(extractedData.person_name || '-'), match: true },
                    { label: 'Cedente (CPF/CNPJ)', operationValue: formatCpfCnpj(assignor?.document), documentValue: formatCpfCnpj(String(extractedData.person_gov_tax_id || '')), match: true },
                    { label: 'Valor no Vencimento', operationValue: formatCurrency(maturityValue), documentValue: formatCurrency(extractedData.operation_value as number), match: true },
                    { label: 'Valor de Desembolso', operationValue: formatCurrency(disbursementValue), documentValue: formatCurrency(extractedData.disbursement_value as number), match: true },
                ]);
            }

            // Set signer validations from backend response
            const sigIssues = validationResult.signerValidation?.issues || [];
            setSignerValidations(filteredSigners.map((s: any) => {
                const issue = sigIssues.find((i: any) => i.field === (s.govTaxId || s.name));
                // Auto-assign signing group ONLY if signer already has it (from fallback)
                // or if signer has NO CPF/CNPJ (is a group placeholder, not an individual PF)
                const roleConfig = loadedRoles.find((r: any) => r.value === s.role);
                const digits = (s.govTaxId || '').replace(/\D/g, '');
                const isIndividualPF = digits.length === 11;
                const sgId = s.signingGroupId || (!isIndividualPF && roleConfig?.signingGroupId) || undefined;
                const sgName = s.signingGroupName || (!isIndividualPF && roleConfig?.signingGroupName) || undefined;
                return {
                    name: s.name,
                    govTaxId: s.govTaxId || '',
                    role: s.role || '',
                    valid: !issue && !s.validationIssue,
                    email: s.email,
                    reason: issue?.message || s.validationIssue || undefined,
                    personName: s.personName || undefined,
                    nameDiverges: s.nameDiverges || false,
                    signingGroupId: sgId,
                    signingGroupName: sgName,
                    anchorName: s.anchorName || undefined,
                };
            }));

            // Set contract validation (structure)
            setContractValidation({
                canSendToSignature: validationResult.canSendToSignature,
                issues: validationResult.structureValidation?.issues || [],
            });

            setStep('comparison');
        } catch (e: any) {
            toast.error(e.message || 'Erro ao validar');
        } finally {
            setLoading(false);
        }
    };

    // Handle signer role change from the validation UI
    const handleSignerRoleChange = (index: number, newRole: string) => {
        const roleConfig = availableRoles.find(r => r.value === newRole);
        const updated = signerValidations.map((s, i) => {
            if (i !== index) return s;
            const hasGroup = !!roleConfig?.signingGroupId;
            const isValidNow = hasGroup || !!s.email;
            return {
                ...s,
                role: newRole,
                signingGroupId: roleConfig?.signingGroupId || undefined,
                signingGroupName: roleConfig?.signingGroupName || undefined,
                valid: isValidNow,
                reason: isValidNow ? undefined : s.reason,
            };
        });
        setSignerValidations(updated);

        // Re-validate structure locally based on current role assignments
        if (availableRoles.length > 0) {
            const roleCounts: Record<string, number> = {};
            updated.forEach(s => { if (s.role) roleCounts[s.role] = (roleCounts[s.role] || 0) + 1; });
            const issues: any[] = [];
            availableRoles.forEach(req => {
                if (!req.required) return;
                // VALIDADOR↔TESTEMUNHA equivalence
                let count = roleCounts[req.value] || 0;
                if (req.value === 'TESTEMUNHA') count += (roleCounts['VALIDADOR'] || 0);
                if (req.value === 'VALIDADOR') count += (roleCounts['TESTEMUNHA'] || 0);
                if (count < (req.minCount || 1)) {
                    issues.push({ message: `${req.label}: necessário ${req.minCount || 1}, encontrado ${count}` });
                }
            });
            setContractValidation({ canSendToSignature: issues.length === 0, issues });
        }
    };

    // Step 3: Confirm and open signature modal
    const handleConfirmAndSend = async () => {
        if (!contractId) return;
        setLoading(true);
        try {
            const signers = signerValidations.map(s => ({
                name: s.name, email: s.email || '', role: s.role, govTaxId: s.govTaxId,
                signing_group_id: s.signingGroupId, signing_group_name: s.signingGroupName,
                anchor_name: (s as any).anchorName || undefined,
            }));
            const contractType = String(extractedData?.operation_type || '');
            const result = await contractApi.updateContract(contractId, {
                signers: signers as Record<string, unknown>[],
                contractType: contractType || undefined,
            });

            // Check if contract is now READY
            if (result.validation && !result.validation.canSendToSignature) {
                setContractValidation(result.validation);
                toast.warn('Estrutura do contrato ainda incompleta');
                setLoading(false);
                return;
            }

            setContractValidation(result.validation);
            setShowSignatureModal(true);
        } catch (e: any) {
            toast.error(e.message || 'Erro ao confirmar');
        } finally {
            setLoading(false);
        }
    };

    const stepIndex = STEPS.findIndex(s => s.key === step);
    const allSignersValid = signerValidations.length > 0 && signerValidations.every(s => s.valid);
    const allComparisonFieldsMatch = comparison.length > 0 && comparison.every(f => f.match);
    const canSend = allSignersValid && allComparisonFieldsMatch && (contractValidation?.canSendToSignature !== false);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            {loading && <Loading />}

            {/* Header */}
            <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4">
                <div className="flex items-center gap-3 max-w-5xl mx-auto">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
                        <FiArrowLeft className="text-gray-600 dark:text-gray-400" />
                    </button>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Enviar Contrato para Assinatura</h1>
                        {operation && <p className="text-sm text-gray-500">Operação: {operation.sequencial_number ?? ''} - {operation.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR' || p.participant_type_name === 'BORROWER')?.name ?? ''}</p>}
                    </div>
                </div>
            </div>

            {/* Steps */}
            <div className="max-w-5xl mx-auto px-6 py-4">
                <StepIndicator steps={STEPS} currentIndex={stepIndex} />
            </div>

            <div className="max-w-5xl mx-auto px-6 pb-8">
                {/* Step 1: Upload */}
                {step === 'upload' && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Upload do Documento</h2>

                        {existingContract && (
                            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                    ⚠️ Esta operação já possui um contrato enviado
                                </p>
                                <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 space-y-1">
                                    <p>📄 Arquivo: <span className="font-medium">{existingContract.fileName}</span></p>
                                    {existingContract.uploadedBy && <p>👤 Enviado por: <span className="font-medium">{existingContract.uploadedBy}</span></p>}
                                    <p>📋 Status: <span className="font-medium">{translateContractStatus(existingContract.status)}</span></p>
                                </div>
                                {existingContract.signatureProcessId && (
                                    <button
                                        onClick={() => navigate(`/signature/${existingContract.signatureProcessId}`)}
                                        className="inline-block mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium cursor-pointer bg-transparent border-none p-0"
                                    >
                                        🔗 Ver processo de assinatura
                                    </button>
                                )}
                            </div>
                        )}

                        {checkingExisting && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">⏳ Verificando contratos anteriores...</p>
                        )}

                        <FileUpload id="contract-sign-pdf" name="document" label="Arquivo PDF:" accept=".pdf" info="Selecione o contrato para enviar" errorMessage="" onUploadFile={setFile} onRemoveFile={() => setFile(null)} />
                        <button onClick={handleUpload} disabled={!file || loading || checkingExisting} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                            Enviar e Extrair Dados
                        </button>
                    </div>
                )}

                {/* Step 2: Extraction result */}
                {step === 'extraction' && extractedData && (
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Dados Extraídos do Documento</h2>

                        <div className="space-y-2 mb-6">
                            {[
                                { label: 'Emitente / Devedor', value: String(extractedData.person_name || '-'), alwaysShow: true },
                                { label: 'CPF / CNPJ do Emitente', value: formatCpfCnpj(String(extractedData.person_gov_tax_id || '')), alwaysShow: true },
                                { label: 'Tipo de Contrato', value: String(extractedData.operation_type || '-').replace(/_/g, ' '), alwaysShow: true },
                                { label: 'Nº do Contrato', value: String(extractedData.contract_number || '-'), alwaysShow: false },
                                { label: 'Data de Emissão', value: String(extractedData.issue_date || 'Não informada'), alwaysShow: true },
                                { label: 'Data de Vencimento', value: String(extractedData.due_date || 'Não informada'), alwaysShow: true },
                                { label: 'Valor no Vencimento', value: formatCurrency(extractedData.operation_value as number), alwaysShow: true },
                                { label: 'Valor de Desembolso', value: formatCurrency(extractedData.disbursement_value as number), alwaysShow: true },
                                { label: 'Taxa de Juros (% a.a.)', value: extractedData.interest_rate ? `${extractedData.interest_rate}%` : '-', alwaysShow: false },
                                { label: 'Indexador', value: String(extractedData.indexer || '-'), alwaysShow: false },
                                { label: 'Credor / Cessionária', value: String(extractedData.creditor_name || '-'), alwaysShow: false },
                                { label: 'CNPJ do Credor', value: extractedData.creditor_gov_tax_id ? formatCpfCnpj(String(extractedData.creditor_gov_tax_id)) : '-', alwaysShow: false },
                                { label: 'Produto (Safra)', value: String(extractedData.crop_description || '-'), alwaysShow: false },
                                { label: 'Quantidade (ton)', value: extractedData.crop_quantity_tons ? String(extractedData.crop_quantity_tons) : '-', alwaysShow: false },
                                { label: 'Garantia', value: String(extractedData.guarantee_description || '-'), alwaysShow: false },
                            ].filter(f => f.alwaysShow || (f.value !== '-' && f.value !== '')).map((f, i) => (
                                <div key={i} className="p-3 rounded-lg border border-gray-200 dark:border-slate-700">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{f.label}</span>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{f.value}</p>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-3">
                            <button onClick={() => setStep('upload')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={handleProceedToComparison} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                {loading ? 'Validando...' : 'Validar e Comparar'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3: Comparison + Validation */}
                {step === 'comparison' && (
                    <div className="space-y-4">
                        {/* Data comparison */}
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Comparação: Operação vs. Documento</h2>
                            <div className="space-y-2">
                                {comparison.map((field, i) => (
                                    <div key={i} className={`p-3 rounded-lg border ${field.match ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span>{field.match ? '✅' : '❌'}</span>
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{field.label}</span>
                                        </div>
                                        {!field.match && (
                                            <div className="ml-7 text-xs space-y-0.5">
                                                <p className="text-gray-600 dark:text-gray-400">Operação: <span className="font-medium">{field.operationValue}</span></p>
                                                <p className="text-red-600 dark:text-red-400">Documento: <span className="font-medium">{field.documentValue}</span></p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Signers validation */}
                        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Signatários</h2>
                            <div className="space-y-2">
                                {signerValidations.map((s, i) => (
                                    <div key={i} className={`flex flex-col gap-2 p-3 rounded-lg border ${s.valid ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20'}`}>
                                        <div className="flex items-center gap-3">
                                            <span>{s.valid ? '✅' : '❌'}</span>
                                            <span className="font-medium text-sm text-gray-900 dark:text-white">{s.name}</span>
                                            <span className="text-sm text-gray-500 dark:text-gray-400">{formatCpfCnpj(s.govTaxId)}</span>
                                            {availableRoles.length > 0 ? (
                                                <div className="w-48">
                                                    <SelectTextField
                                                        id={`role-${i}`}
                                                        name={`role-${i}`}
                                                        label=""
                                                        value={s.role}
                                                        onChange={e => handleSignerRoleChange(i, e.target.value)}
                                                        items={availableRoles}
                                                        emptyLabel="Selecione o papel..."
                                                    />
                                                </div>
                                            ) : (
                                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs">{s.role}</span>
                                            )}
                                            {!s.valid && <span className="text-xs text-red-600 dark:text-red-400 ml-auto">{s.reason}</span>}
                                            {s.valid && s.email && <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">📧 {s.email}</span>}
                                        </div>
                                        {s.signingGroupId && (
                                            <div className="ml-8 flex items-center gap-2">
                                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded text-xs font-medium">
                                                    Grupo: {s.signingGroupName || s.signingGroupId}
                                                </span>
                                                <span className="text-xs text-gray-400 italic">Assinatura via grupo DocuSign</span>
                                            </div>
                                        )}
                                        {s.nameDiverges && s.personName && (
                                            <div className="ml-8 p-2 rounded border border-amber-200 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20">
                                                <p className="text-xs text-amber-700 dark:text-amber-300 font-medium mb-1">
                                                    Nome divergente entre documento e cadastro:
                                                </p>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <button
                                                        onClick={() => {
                                                            const updated = [...signerValidations];
                                                            updated[i] = { ...updated[i], name: updated[i].personName!, nameDiverges: false, personName: undefined };
                                                            setSignerValidations(updated);
                                                        }}
                                                        className="px-2 py-1 text-xs border border-green-300 bg-green-50 text-green-700 dark:border-green-600 dark:bg-green-900/30 dark:text-green-300 rounded hover:bg-green-100 transition-colors"
                                                    >
                                                        Usar cadastro: {s.personName}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const updated = [...signerValidations];
                                                            updated[i] = { ...updated[i], nameDiverges: false, personName: undefined };
                                                            setSignerValidations(updated);
                                                        }}
                                                        className="px-2 py-1 text-xs border border-gray-300 bg-white text-gray-600 dark:border-slate-600 dark:bg-slate-700 dark:text-gray-300 rounded hover:bg-gray-100 transition-colors"
                                                    >
                                                        Manter documento: {s.name}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Structure validation */}
                        {contractValidation && contractValidation.issues && contractValidation.issues.length > 0 && (
                            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-700 dark:text-red-300 font-medium">⚠️ Estrutura do contrato incompleta:</p>
                                {contractValidation.issues.map((issue: any, i: number) => (
                                    <p key={i} className="text-xs text-red-600 dark:text-red-400 ml-4 mt-1">{issue.message}</p>
                                ))}
                            </div>
                        )}

                        {!allComparisonFieldsMatch && comparison.length > 0 && (
                            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">⚠️ Divergências encontradas na comparação:</p>
                                {comparison.filter(f => !f.match).map((f, i) => (
                                    <p key={i} className="text-xs text-amber-600 dark:text-amber-400 ml-4 mt-1">{f.label}: Operação ({f.operationValue}) ≠ Documento ({f.documentValue})</p>
                                ))}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button onClick={() => setStep('extraction')} className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-gray-300">Voltar</button>
                            <button onClick={handleConfirmAndSend} disabled={!canSend}
                                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                                Confirmar e Enviar para Assinatura
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Signature Modal (opens over comparison step) */}
            {showSignatureModal && (
                <SendToSignatureModal
                    contractId={contractId}
                    operationNumber={operation?.operation_number || ''}
                    fileName={file?.name || ''}
                    onClose={() => { setShowSignatureModal(false); }}
                    onSuccess={(processId) => {
                        setShowSignatureModal(false);
                        toast.success('Enviado para assinatura!');
                        navigate(`/signature/${processId}`);
                    }}
                />
            )}
        </div>
    );
};

export default ContractSignPage;
